/**
 * Internals for the Binom draft hook: live→draft mapper + dirty snapshot.
 * Kept out of the hook file to satisfy the file-size budget.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { Flow } from '@/hooks/voluum/useVoluum';
import type { BinomCampaignDetailRaw } from '@/hooks/binom';

import type {
  BinomCampaignMetaDraft,
  BinomDraftItem,
  BinomDraftPath,
  BinomDraftPathGroup,
  BinomFlowDraft,
} from './binom-flow-draft.types';

export type DraftMutator = (d: BinomFlowDraft) => void;

export function liveToDraft(
  live: Flow | undefined,
  liveCampaign: BinomCampaignDetailRaw | null | undefined,
): BinomFlowDraft | null {
  if (!live) return null;
  const meta = extractMeta(liveCampaign);
  return {
    id: String(live.id ?? ''),
    name: String(live.name ?? ''),
    defaultPaths: (live.defaultPaths ?? []).map(toDraftPath),
    pathGroups: (live.pathGroups ?? []).map(toDraftGroup),
    originalCampaignMeta: meta,
    campaignMeta: { ...meta },
  };
}

function extractMeta(c: BinomCampaignDetailRaw | null | undefined): BinomCampaignMetaDraft {
  if (!c) return {};
  const costModelRaw = c.cost?.model ?? c.costModel;
  return {
    name: c.name,
    key: c.key,
    groupUuid: c.groupUuid,
    domainUuid: c.domainUuid,
    trafficSourceId: c.trafficSourceId,
    costModel:
      costModelRaw === 'CPC' || costModelRaw === 'CPM' || costModelRaw === 'CPA'
        ? costModelRaw
        : undefined,
    currency: c.cost?.money?.currency ?? c.currency,
    amount: c.cost?.money?.amount ?? c.amount,
    isAuto: c.cost?.isAuto ?? c.isAuto,
    distributionType: c.distributionType,
    hideReferrerType: c.hideReferrer?.type ?? c.hideReferrerType,
    hideReferrerDomain: c.hideReferrer?.domain ?? c.hideReferrerDomain,
    tags: c.tags,
    vpnProxyDetection: c.vpnProxyDetection,
    botDetection: c.botDetection,
    notes: c.notes,
    campaignSettings: c.campaignSettings,
  };
}

function toDraftGroup(g: NonNullable<Flow['pathGroups']>[number]): BinomDraftPathGroup {
  return {
    id: String(g.id ?? ''),
    name: String(g.name ?? ''),
    paths: (g.paths ?? []).map(toDraftPath),
  };
}

type LiveFlowPath = NonNullable<Flow['defaultPaths']>[number];

function toDraftPath(p: LiveFlowPath): BinomDraftPath {
  const active = p.active !== false;
  return {
    id: String(p.id ?? ''),
    name: String(p.name ?? ''),
    originalName: String(p.name ?? ''),
    weight: Number(p.weight ?? 0),
    originalWeight: Number(p.weight ?? 0),
    active,
    originalActive: active,
    landers: (p.landers ?? []).map((l) => toDraftItem(l)),
    offers: (p.offers ?? []).map((o) => toDraftItem(o)),
  };
}

function toDraftItem(i: {
  id: string;
  name: string;
  weight: number;
  active?: boolean;
}): BinomDraftItem {
  const active = i.active !== false;
  return {
    id: String(i.id),
    name: String(i.name),
    weight: Number(i.weight ?? 0),
    originalWeight: Number(i.weight ?? 0),
    active,
    originalActive: active,
  };
}

export function snapshotForDirty(draft: BinomFlowDraft): string {
  return JSON.stringify({
    name: draft.name,
    defaults: draft.defaultPaths.map(snapPath),
    groups: draft.pathGroups.map((g) => ({ id: g.id, paths: g.paths.map(snapPath) })),
    campaignMeta: draft.campaignMeta,
  });
}

function snapPath(p: BinomDraftPath) {
  return {
    id: p.id,
    name: p.name,
    weight: p.weight,
    active: p.active,
    landers: p.landers.map((l) => [l.id, l.weight, l.active]),
    offers: p.offers.map((o) => [o.id, o.weight, o.active]),
  };
}

/** Returns only the fields user changed vs the original. */
export function diffCampaignMeta(draft: BinomFlowDraft): BinomCampaignMetaDraft {
  const out: BinomCampaignMetaDraft = {};
  const orig = draft.originalCampaignMeta;
  const cur = draft.campaignMeta;
  (Object.keys(cur) as Array<keyof BinomCampaignMetaDraft>).forEach((k) => {
    if (JSON.stringify(cur[k]) !== JSON.stringify(orig[k])) {
      // assigning whole field — narrower per-key typing is overkill here
      (out as Record<string, unknown>)[k as string] = cur[k];
    }
  });
  return out;
}
